/*
  Centralized cache manager for ETag + localStorage backed responses.
  Goals:
  - Provide explicit invalidation after critical mutations
  - Provide a short-lived "force revalidate" window to bypass If-None-Match

  Keys used across services today:
  - Requests list:   reqs:etag:<url>, reqs:data:<url>
  - Request by id:   request:etag:<url>, request:data:<url>
  - Request bids:    request-bids:etag:<url>, request-bids:data:<url>
  - Buyer bids:      bids:etag:<url>, bids:data:<url>
*/

let forceRevalidateUntil = 0;
const SNAPSHOT_PREFIX = 'snapshot:';

export const cacheManager = {
  // Activate a bypass window (ms) during which GETs should skip If-None-Match
  forceRevalidateFor(ms: number) {
    forceRevalidateUntil = Date.now() + Math.max(0, ms);
  },
  shouldBypass(): boolean {
    return Date.now() < forceRevalidateUntil;
  },

  // Generic helpers to remove cached keys by prefix match
  removeByPrefix(prefix: string) {
    try {
      const toRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i) as string;
        if (k && k.startsWith(prefix)) toRemove.push(k);
      }
      toRemove.forEach((k) => localStorage.removeItem(k));
    } catch {}
  },

  // Requests list can be parametric; easiest robust path is nuking all list caches
  invalidateRequestsList() {
    this.removeByPrefix('reqs:etag:');
    this.removeByPrefix('reqs:data:');
  },

  invalidateRequest(idOrRef: string, baseUrl: string) {
    try {
      const url = `${baseUrl}/requests/${encodeURIComponent(idOrRef)}`;
      localStorage.removeItem(`request:etag:${url}`);
      localStorage.removeItem(`request:data:${url}`);
    } catch {}
  },

  invalidateRequestBids(idOrRef: string, baseUrl: string) {
    try {
      const url = `${baseUrl}/requests/${encodeURIComponent(idOrRef)}/bids`;
      localStorage.removeItem(`request-bids:etag:${url}`);
      localStorage.removeItem(`request-bids:data:${url}`);
    } catch {}
  },

  // Buyer bids list (flattened) – parametric; clear all to keep simple and safe
  invalidateBuyerBids() {
    this.removeByPrefix('bids:etag:');
    this.removeByPrefix('bids:data:');
  },

  // Orders: we currently don’t cache orders in localStorage, but keep hook-friendly API
  invalidateOrders() {
    // no-op for now; presence kept for symmetry and future ETag adoption
  },

  // Generic snapshot helpers for instant UI hydration
  getSnapshot<T = any>(key: string): { data: T; timestamp: number } | null {
    try {
      const raw = localStorage.getItem(`${SNAPSHOT_PREFIX}${key}`);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (typeof parsed !== 'object' || parsed === null || !('data' in parsed)) return null;
      return parsed as { data: T; timestamp: number };
    } catch {
      return null;
    }
  },
  setSnapshot(key: string, data: any) {
    try {
      const payload = JSON.stringify({ data, timestamp: Date.now() });
      localStorage.setItem(`${SNAPSHOT_PREFIX}${key}`, payload);
    } catch {
      // ignore quota errors
    }
  },
  clearSnapshot(key: string) {
    try {
      localStorage.removeItem(`${SNAPSHOT_PREFIX}${key}`);
    } catch {}
  },
};

export default cacheManager;
